import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import { apiSlice } from "./apiSlice";
import { UnitConverter } from "../../components/MeasurementUnits/UnitConverter";
import type { IGasRateCriticalCalculator } from "../../calculators/GasRateCritical/interfaces";
import type { unitId } from "../../interfaces/interfaces";


export const initialState: IGasRateCriticalCalculator = {
	inputs: [
		// {
		// 	id: 'grc-input-pressure-wellhead',
		// 	type: 'string',
		// 	label: '',
		// 	queryName: 'pressure_wh',
		// 	baseUnitId: 'bar',
		// 	baseValue: 100,
		// 	currentValue: 100,
		// 	currentUnitId: 'bar',
		// 	unitCategoryId: 'pressure',
		// 	userValue: null,
		// 	userUnitId: null,
		// 	min: 0,
		// },
		{
			id: 'grc-input-pressure-reservoir',
			type: 'string',
			label: '',
			queryName: 'pressure_res',
			baseUnitId: 'bar',
			baseValue: 300,
			currentValue: 300,
			currentUnitId: 'bar',
			unitCategoryId: 'pressure',
			userValue: null,
			userUnitId: null,
			min: 0
		},
		// {
		// 	id: 'grc-input-temperature-reservoir',
		// 	type: 'string',
		// 	label: '',
		// 	queryName: 'temperature_res',
		// 	baseUnitId: 'celsius',
		// 	baseValue: 80,
		// 	currentValue: 80,
		// 	currentUnitId: 'celsius',
		// 	unitCategoryId: 'temperature',
		// 	userValue: null,
		// 	userUnitId: null,
		// 	min: -273
		// },
		// {
		// 	id: 'grc-input-tubing-id',
		// 	type: 'string',
		// 	label: '',
		// 	queryName: 'tubing_id',
		// 	baseUnitId: 'meter',
		// 	baseValue: 0.062,
		// 	currentValue: 0.062,
		// 	currentUnitId: 'meter',
		// 	unitCategoryId: 'length',
		// 	userValue: null,
		// 	userUnitId: null,
		// 	min: 0
		// },
		{
			id: 'grc-input-specific-gravity',
			type: 'string',
			label: '',
			queryName: 'liquid_specific_gravity',
			baseUnitId: null,
			baseValue: 1.015,
			currentValue: 1.015,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
		},
		{
			id: 'grc-input-liquid-gas-surface-tension',
			type: 'string',
			label: '',
			queryName: 'liquid_gas_surface_tension',
			baseUnitId: 'dynescm',
			baseValue: 60,
			currentValue: 60,
			currentUnitId: 'dynescm',
			unitCategoryId: 'surface_tension',
			userValue: null,
			userUnitId: null,
		},
		{
			id: 'grc-input-pressure-wf',
			type: 'string',
			label: '',
			queryName: 'pressure_wf',
			baseUnitId: 'bar',
			baseValue: 240,
			currentValue: 240,
			currentUnitId: 'bar',
			unitCategoryId: 'pressure',
			userValue: null,
			userUnitId: null,
			max: 500,
			min: 1.01325,
		},
	],

	requiredSubscriptionLevel: "free",
};

const converter = new UnitConverter();

const gasRateCriticalCalculatorSlice = createSlice({
	name: "gasRateCriticalCalculator",
	initialState,
	selectors: {
	},
	reducers: {
		setInputValue: (state, action: PayloadAction<{ inputName: string, value: number | null }>) => {
			state.inputs.forEach(i => {
				if (i.queryName === action.payload.inputName) {
					i.currentValue = action.payload.value;
					i.baseValue = (i.unitCategoryId && action.payload.value != null) ? converter.convert(action.payload.value, i.currentUnitId, i.baseUnitId) : action.payload.value;
					i.userValue = action.payload.value;
					i.userUnitId = i.currentUnitId;
				}
			});

			state.results1 = undefined;
			state.results2 = undefined;
			state.chartData1 = undefined;
			state.chartData2 = undefined;
		},
		updateInputByUnit: (state, action: PayloadAction<{ inputId: string, selectedUnitId: unitId | null }>) => {
			state.inputs.forEach(i => {
				if (i.id === action.payload.inputId) {
					i.currentValue = i.currentValue != null ? converter.convert(i.currentValue, i.currentUnitId, action.payload.selectedUnitId) : null;
					i.currentUnitId = action.payload.selectedUnitId;
					i.baseValue = i.userValue && i.userUnitId
						? converter.convert(i.userValue, i.userUnitId, i.baseUnitId)
						: converter.convert(i.currentValue ?? 0, i.currentUnitId, i.baseUnitId);
					return;
				}
			})
		},
		validateInputs: (state) => {
			state.inputs.forEach(input => {
				if (input.min != null && input.currentValue != 0 && input.currentValue != null && input.currentValue < input.min) {
					input.error = "validation.min_value_error";
					input.errorParams = { min: input.min };
				} else if (input.max != null && input.currentValue != null && input.currentValue > input.max) {
					input.error = "validation.max_value_error";
					input.errorParams = { max: input.max };
				} else {
					input.error = null;
					input.errorParams = null;
				}
			});
		},
	},
	extraReducers: (builder) => {
		builder
			.addMatcher(apiSlice.endpoints.calculator.matchFulfilled, (state, action) => {
				const params = action.meta.arg.originalArgs as Record<string, string>;
				if (!params?.calculator_type) {
					console.warn("No calculator_type in calculator response");
					return;
				}
				if (params.calculator_type != "gas_rate_critical") {
					return;
				}

				if (!action?.payload?.results?.p || !action?.payload?.results?.q) {
					console.warn("No results in calculator response");
					return;
				}

				if (params.well === "1") {
					state.results1 = action?.payload?.results;
				}
				if (params.well === "2") {
					state.results2 = action?.payload?.results;
				}
			})
	}
})

export default gasRateCriticalCalculatorSlice;

export const {
	setInputValue,
	updateInputByUnit,
	validateInputs
} = gasRateCriticalCalculatorSlice.actions;
